/**
 * ConfirmTrashDialog
 * Modal to confirm moving a document to trash (soft-delete).
 */

import { AlertTriangle } from 'lucide-react';

interface ConfirmTrashDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  documentTitle: string;
  isPermanent?: boolean;
}

export function ConfirmTrashDialog({
  open,
  onClose,
  onConfirm,
  documentTitle,
  isPermanent = false,
}: ConfirmTrashDialogProps) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="trash-dialog-title"
          className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5 pointer-events-auto"
        >
          {/* Icon */}
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-full ${isPermanent ? 'bg-red-100' : 'bg-amber-100'}`}>
              <AlertTriangle size={20} className={isPermanent ? 'text-red-600' : 'text-amber-600'} />
            </div>
            <h3 id="trash-dialog-title" className="text-base font-semibold text-neutral-900">
              {isPermanent ? 'Permanently Delete?' : 'Move to Trash?'}
            </h3>
          </div>

          {/* Message */}
          <p className="text-sm text-neutral-600 mb-5 ml-11">
            {isPermanent ? (
              <>
                <span className="font-medium">"{documentTitle}"</span> will be permanently deleted.
                This action cannot be undone.
              </>
            ) : (
              <>
                Move <span className="font-medium">"{documentTitle}"</span> to trash?
                You can restore it later from the Trash page.
              </>
            )}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                isPermanent
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-coral-500 hover:bg-coral-600'
              }`}
            >
              {isPermanent ? 'Delete Forever' : 'Move to Trash'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
